import React from 'react';
import Markdown, { Components } from 'react-markdown';
import { useHistory } from 'react-router-dom';
import { usePatronSelector } from '../../common/use-patron-selector';
import {
  isInternalUrl,
  isLegacyLink,
  convertLegacyAssistantUrl,
} from '../../lib/url-utils';
import { Link } from '../ui';
import useAssistantActionHandlers from '../../common/use-assistant-action-handlers';
import { NavigationReferer } from '../assistant/assistant-action';
import { ActionNavigationType } from '../../models/assistant/interfaces/Navigation';
import { AssistantLink } from '../assistant/assistant-link';
import './ai-content.scss';

interface MarkdownLinkProps {
  href?: string;
  children: React.ReactNode;
  onLinkClick?: (url: string, isInternal: boolean) => void;
}

/**
 * MarkdownLink component handles different types of links in AI-generated markdown:
 * 1. Assistant links (modern and legacy SC4) - routed through AssistantLink
 * 2. Legacy SC4 hash links - converted to path-based navigation
 * 3. Regular internal links - routed with history.push
 * 4. External links - opened in new tab
 */
export const MarkdownLink: React.FC<MarkdownLinkProps> = ({
  href,
  children,
  onLinkClick,
}) => {
  const program = usePatronSelector((state) => state.program);
  const history = useHistory();
  const { getDeeplink } = useAssistantActionHandlers();

  if (!href) {
    return (
      <Link href="" className="ai-content__markdown-link">
        {children}
      </Link>
    );
  }

  // Check if this is a legacy SC4 assistant URL and convert it
  const convertedUrl = convertLegacyAssistantUrl(href);
  const finalUrl = convertedUrl ?? href;

  const isInternal = isInternalUrl(finalUrl, {
    rootPath: program.root_path,
    isCustomDomain: program.is_custom_domain,
  });

  // Check if this is an assistant URL (either converted from legacy or modern format)
  const isAssistantLink =
    convertedUrl !== null ||
    finalUrl.includes('/assistant/') ||
    finalUrl.includes('#assistant/');

  // Handle assistant links with AssistantLink component
  if (isInternal && isAssistantLink) {
    try {
      // Use getDeeplink to determine the proper navigation for assistant URLs
      const deeplink = getDeeplink(
        { url: finalUrl, type: ActionNavigationType.DEEPLINK, title: '' },
        { referer: NavigationReferer.INBOX }
      );

      return (
        <AssistantLink
          to={deeplink.to ?? deeplink.url}
          className="ai-content__markdown-link"
          onClick={() => onLinkClick?.(finalUrl, isInternal)}
        >
          {children}
        </AssistantLink>
      );
    } catch (error) {
      console.error('Error handling assistant link:', error);
    }
  }

  // Handle click for internal non-assistant links, especially legacy sc4 links
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onLinkClick?.(finalUrl, isInternal);

    if (!isInternal || !finalUrl) return;

    try {
      const url = new URL(finalUrl, window.location.origin);
      const isLegacy = isLegacyLink(url);

      if (isLegacy) {
        // Convert legacy sc4 hash links to path-based links
        // e.g., /sc4#service/4/command/... -> /sc4/service/4/command/...
        e.preventDefault();
        const path = `/sc4${url.hash}${url.search}`;
        history.push(path);
      }
      // For non-legacy internal links, the action="push" prop handles navigation
    } catch (error) {
      console.error('Error handling link click:', error);
    }
  };

  // For non-assistant internal links and external links, use regular Link
  return (
    <Link
      href={finalUrl}
      target={isInternal ? undefined : '_blank'}
      action={isInternal ? 'push' : undefined}
      onClick={handleClick}
      className="ai-content__markdown-link"
    >
      {children}
    </Link>
  );
};

interface AiMarkdownProps {
  content: string;
  className?: string;
  onLinkClick?: (url: string, isInternal: boolean) => void;
}

/**
 * Reusable markdown renderer for AI-generated content (chatbot messages, AI summaries).
 * Supports:
 * - Headings (h1-h4)
 * - Lists (ordered and unordered)
 * - Text formatting (bold, italic)
 * - Links (internal and external, including legacy SC4 assistant links)
 * - Code blocks and inline code
 * - Blockquotes
 * - Horizontal rules
 */
const staticComponents = {
  p: ({ children }) => <p className="ai-content__markdown-p">{children}</p>,
  ul: ({ children }) => <ul className="ai-content__markdown-ul">{children}</ul>,
  ol: ({ children }) => <ol className="ai-content__markdown-ol">{children}</ol>,
  li: ({ children }) => <li className="ai-content__markdown-li">{children}</li>,
  strong: ({ children }) => (
    <strong className="ai-content__markdown-strong">{children}</strong>
  ),
  em: ({ children }) => <em className="ai-content__markdown-em">{children}</em>,
  h1: ({ children }) => <h1 className="ai-content__markdown-h1">{children}</h1>,
  h2: ({ children }) => <h2 className="ai-content__markdown-h2">{children}</h2>,
  h3: ({ children }) => <h3 className="ai-content__markdown-h3">{children}</h3>,
  h4: ({ children }) => <h4 className="ai-content__markdown-h4">{children}</h4>,
  blockquote: ({ children }) => (
    <blockquote className="ai-content__markdown-blockquote">
      {children}
    </blockquote>
  ),
  code: ({ children }) => (
    <code className="ai-content__markdown-code">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="ai-content__markdown-pre">{children}</pre>
  ),
  hr: () => <hr className="ai-content__markdown-hr" />,
} as Components;

const AiMarkdown: React.FC<AiMarkdownProps> = ({
  content,
  className = 'ai-content__markdown',
  onLinkClick,
}) => {
  const components = {
    ...staticComponents,
    a: ({ children, href }) => (
      <MarkdownLink href={href} onLinkClick={onLinkClick}>
        {children}
      </MarkdownLink>
    ),
  } as Components;

  return (
    <div className={className} data-testid="playwright-ai-markdown">
      <Markdown components={components}>{content}</Markdown>
    </div>
  );
};

export default AiMarkdown;
